import React, { useRef, useState } from 'react';

import { X } from 'lucide-react';

import { STATEMENT_UPLOAD_ACCEPT } from '../utils/statementUploadFormats';

// v9 .label / .input / .btn
const LABEL = 'mb-2 block text-sm font-medium text-gray-700';
const INPUT =
	'block w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm leading-5 text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed';
const BTN =
	'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium leading-5 transition-colors disabled:cursor-not-allowed disabled:opacity-50';

const sizeText = (bytes: number) =>
	bytes >= 1024 * 1024
		? `${(bytes / 1024 / 1024).toFixed(1)} MB`
		: `${Math.max(1, Math.round(bytes / 1024))} KB`;

interface StatementUploadV9Props {
	files: File[];
	error: string;
	configs: { id?: string; name: string }[];
	configId: string;
	onConfigChange: (id: string) => void;
	onFiles: (files: File[]) => void;
	onCancel: () => void;
	onSubmit: (e: React.FormEvent) => void;
	uploading: boolean;
	// Several non-photo files are months of one merchant (ROC-104): the name is then required.
	monthsMode: boolean;
	/** The tenant has merchant records (feature flag merchant_records). */
	monthsAllowed?: boolean;
	merchantName: string;
	onMerchantNameChange: (name: string) => void;
}

/** The v9 "Upload Statement" modal (ROC-116): no statement type, the template picks the pricing. */
const StatementUploadV9: React.FC<StatementUploadV9Props> = ({
	files,
	error,
	configs,
	configId,
	onConfigChange,
	onFiles,
	onCancel,
	onSubmit,
	uploading,
	monthsMode,
	monthsAllowed = false,
	merchantName,
	onMerchantNameChange,
}) => {
	const picker = useRef<HTMLInputElement>(null);
	const [dragActive, setDragActive] = useState(false);
	const drag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(e.type === 'dragenter' || e.type === 'dragover');
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="upload-statement-title"
				className="flex max-h-[90vh] w-full max-w-[560px] flex-col rounded-lg bg-white shadow-[0_20px_25px_-5px_rgba(0,0,0,.1)]"
			>
				<div className="flex items-center justify-between border-b border-gray-200 p-6">
					<h4
						id="upload-statement-title"
						className="text-xl font-semibold text-gray-900"
					>
						Upload Statement
					</h4>
					<button
						type="button"
						className="flex h-8 w-8 items-center justify-center rounded-md text-primary-500"
						onClick={onCancel}
						aria-label="Close"
					>
						<X className="h-4 w-4" aria-hidden="true" />
					</button>
				</div>
				<div className="space-y-4 overflow-auto p-6">
					<div
						role="button"
						tabIndex={0}
						className={`cursor-pointer rounded-lg border-2 border-dashed p-7 text-center text-gray-600 ${
							dragActive
								? 'border-blue-500 bg-[#f3faff]'
								: 'border-gray-300 bg-gray-50 hover:border-blue-500 hover:bg-[#f3faff]'
						}`}
						onClick={() => picker.current?.click()}
						onKeyDown={(e) => e.key === 'Enter' && picker.current?.click()}
						onDragEnter={drag}
						onDragOver={drag}
						onDragLeave={drag}
						onDrop={(e) => {
							drag(e);
							if (!uploading) onFiles(Array.from(e.dataTransfer.files ?? []));
						}}
					>
						<div>
							<b className="font-medium text-primary-700">
								Drop statements here
							</b>{' '}
							or click to browse
						</div>
						<div className="mt-2 text-xs text-gray-500">
							PDF, XLSX, CSV, MSG, JPEG
							{monthsAllowed && ' · up to three months for one merchant'}
						</div>
						<input
							ref={picker}
							type="file"
							multiple
							hidden
							accept={STATEMENT_UPLOAD_ACCEPT}
							disabled={uploading}
							onChange={(e) => {
								onFiles(Array.from(e.target.files ?? []));
								e.target.value = '';
							}}
						/>
					</div>
					{files.length > 0 && (
						<div className="flex flex-col gap-1.5">
							{files.map((file) => (
								<div
									key={`${file.name}-${file.size}`}
									className="flex items-center gap-2.5 rounded-lg border border-gray-200 px-2.5 py-2 text-[13px]"
								>
									<span className="truncate">{file.name}</span>
									<span className="ml-auto text-xs text-gray-500">
										{sizeText(file.size)}
									</span>
								</div>
							))}
						</div>
					)}
					{error && <p className="text-sm text-red-700">{error}</p>}
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className={LABEL} htmlFor="up-partner">
								Partner
							</label>
							<select id="up-partner" className={INPUT} disabled>
								<option>—</option>
							</select>
						</div>
						<div>
							<label className={LABEL} htmlFor="up-template">
								Pricing template
							</label>
							<select
								id="up-template"
								className={INPUT}
								value={configId}
								disabled={uploading}
								onChange={(e) => onConfigChange(e.target.value)}
							>
								{configs.map((config) => (
									<option key={config.id} value={config.id}>
										{config.name}
									</option>
								))}
							</select>
						</div>
					</div>
					<div>
						<label className={LABEL} htmlFor="up-merchant">
							Merchant
						</label>
						{monthsMode ? (
							<input
								id="up-merchant"
								className={INPUT}
								placeholder="Merchant name"
								value={merchantName}
								onChange={(e) => onMerchantNameChange(e.target.value)}
								disabled={uploading}
								maxLength={255}
								required
							/>
						) : (
							<input
								id="up-merchant"
								className={INPUT}
								placeholder="Read from the statement"
								disabled
							/>
						)}
						<div className="mt-2 text-xs text-gray-500">
							{monthsMode
								? `${files.length} months for one merchant. They become one merchant record.`
								: 'Read from the statement.'}
						</div>
					</div>
					<div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
						For phone photos: line up the page edges with the frame and tap to
						focus. Blurry or missing pages are sent back to you.
					</div>
				</div>
				<div className="flex justify-end gap-3 border-t border-gray-200 p-6">
					<button
						type="button"
						className={`${BTN} border-gray-300 bg-white text-gray-900 hover:bg-gray-100`}
						onClick={onCancel}
						disabled={uploading}
					>
						Cancel
					</button>
					<button
						type="button"
						className={`${BTN} border-transparent bg-primary-500 text-white hover:bg-primary-600`}
						onClick={onSubmit}
						disabled={uploading || files.length === 0}
					>
						{uploading
							? 'Uploading…'
							: monthsMode
								? `Upload ${files.length} statements`
								: 'Upload'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default StatementUploadV9;
